import React, { useEffect, useMemo } from "react";
import { apiRequestContractHandler } from "shared/api/apiRequestContractHandler";
import { postSocietyWorkflowInstanceContract } from "shared/api/contracts/society/societyId/workflow/workflowId/instance";
import {
  CustomRouterType,
  useCustomRouter,
} from "shared/context/CustomRouterContext";
import {
  CustomSessionType,
  useCustomSession,
} from "shared/context/CustomSessionContext";
import { StorePageRouteParams } from "shared/types/HubPageRouteParams";
import { useProductGrid } from "hub/src/components/store/ProductGridContext";
import ProductGridItem from "hub/src/components/store/ProductGridItem";
import { useCallbackRef } from "hub/src/hooks/useCallbackRef";
import { getLowestPrice } from "hub/src/utils/getLowestPrice";

export interface GridProduct {
  productId: string;
  name: string;
  image?: string;
  description?: string;
  longDescription?: string;
  available?: boolean;
  price: number;
  pricing?: Record<string, number>;
  gridId: number;
}

interface ProductGridProps {
  gridId: number;
  params?: StorePageRouteParams;
  title?: string;
  products: GridProduct[];
}

export const handleProductBuyNow = async (
  product: GridProduct,
  session: CustomSessionType,
  workflowId: number,
  router: CustomRouterType,
) => {
  const workflowInstanceStopped = await apiRequestContractHandler(
    postSocietyWorkflowInstanceContract,
    {
      params: {
        societyId: session.societyId!,
        workflowId,
      },
      body: {
        payloadValue: {
          store_product: {
            productId: parseInt(product.productId),
            singleChoiceProductQuestion: parseInt(product.productId),
            price: getLowestPrice(product, session.tags ?? []),
          },
          profileId: session.profileId,
        },
        eventName: "BuyNow",
      },
    },
  );

  if (workflowInstanceStopped.userhubRedirect) {
    router.push(
      workflowInstanceStopped.userhubRedirect,
      workflowInstanceStopped,
    );
  }
};

export const ProductGrid: React.FC<ProductGridProps> = ({
  gridId,
  params,
  title,
  products,
}) => {
  const session = useCustomSession();
  const router = useCustomRouter();

  const {
    fullProductList,
    setFullProductList,
    productGridId,
    productGridSearch,
  } = useProductGrid();

  if (!params?.workflowId) {
    throw new Error("Workflow ID is required in Page Parameters");
  }

  useEffect(() => {
    setFullProductList((prevProducts) => [
      ...prevProducts,
      ...products.map((product) => ({ ...product, gridId })),
    ]);
  }, []);

  const productsList = useMemo(() => {
    if (!productGridSearch) return fullProductList;

    return fullProductList.filter((product) =>
      product.name.toLowerCase().includes(productGridSearch.toLowerCase()),
    );
  }, [productGridSearch, fullProductList]);

  const handleProductBuy = useCallbackRef(async (product: GridProduct) => {
    return handleProductBuyNow(product, session, params.workflowId, router);
  });

  const protectedHandleProductBuyNow =
    session.withAuthentification(handleProductBuy);

  if ((productGridId && productGridId !== gridId) || !productsList.length) {
    return null;
  }

  return (
    <div className="mx-auto">
      {title && title !== "" && (
        <div className="mb-2">
          <h3 className="mb-2">{title}</h3>
          <hr className="text-neutral-mid-200" />
        </div>
      )}

      {productsList.length ? (
        <div className="pb-3 grid grid-cols-1 min-sm:grid-cols-2 min-md:grid-cols-3 min-xl:grid-cols-4 min-2xl:grid-cols-5 gap-4">
          {productsList.map((product, index) => {
            if (product.gridId !== gridId) return null;
            return (
              <ProductGridItem
                key={product.productId}
                index={index}
                product={product}
                classNames={params?.classNames}
                onClickViewDetails={(product) =>
                  router.push(`/store/${product.productId}`)
                }
                onClickBuy={async (product) => {
                  if (!product.available) return;

                  await protectedHandleProductBuyNow(product);
                }}
              />
            );
          })}
        </div>
      ) : (
        <div className="mt-20 w-full flex justify-center">
          No products found
        </div>
      )}
    </div>
  );
};
